/*
 * File: date-helper.ts
 * File Created: 2024-01-04 09:44:26
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-09-05 07:10:45
 * Modified By: Val Liu
 * -----
 */

import { ObjectHelper } from "./object-helper";
import { StringHelper } from "./string-helper";

let lang;
try {
    lang = require("@root/core/i18n/core").default;
} catch (error) {
    /// fallback
}

export class DateHelper {
    static isInvalid(date: Date): boolean {
        return isNaN(date.getTime());
    }

    /// how many days between day1 and day2
    static days(day1: Date, day2: Date): number {
        // Convert both dates to milliseconds
        var startDateMs = day1.getTime();
        var endDateMs = day2.getTime();

        // Calculate the difference in milliseconds
        var differenceMs = endDateMs - startDateMs;

        // Convert back to days and return
        return Math.abs(Math.ceil(differenceMs / (1000 * 60 * 60 * 24)));
    }

    /// how many years between year1 and year2
    static years(year1: Date, year2: Date): number {
        // Calculate the number of days between the two dates
        const daysBetween = this.days(year1, year2);

        // Average number of days per year (taking into account leap years)
        const daysPerYear = 365.25;

        // Calculate the difference in years as a decimal
        const yearsDifference = daysBetween / daysPerYear;

        // Return the result rounded to two decimal places
        return Math.round(yearsDifference * 100) / 100;
    }

    static dayStart(date?: Date): Date {
        let o = date ? new Date(date) : new Date();
        o.setHours(0, 0, 0, 0);
        return o;
    }

    static dayEnd(date?: Date): Date {
        let o = date ? new Date(date) : new Date();
        o.setHours(23, 59, 59, 999);
        return o;
    }

    static monthStart(date?: Date): Date {
        let o = date ? new Date(date) : new Date();
        o.setDate(1); // Set to the first day of the month
        o.setHours(0, 0, 0, 0); // Start of the day
        return o;
    }

    static monthEnd(date?: Date): Date {
        let o = date ? new Date(date) : new Date();
        o.setMonth(o.getMonth() + 1); // Move to the next month
        o.setDate(0); // Set to the last day of the current month (day 0 of the next month is the last day of the current month)
        o.setHours(23, 59, 59, 999); // End of the day
        return o;
    }

    static yearStart(date?: Date): Date {
        let o = date ? new Date(date) : new Date();
        o.setMonth(0); // Set to January
        o.setDate(1); // Set to the first day of the month
        o.setHours(0, 0, 0, 0); // Start of the day
        return o;
    }

    static yearEnd(date?: Date): Date {
        let o = date ? new Date(date) : new Date();
        o.setMonth(11); // Set to December
        o.setDate(31); // Set to the last day of the month
        o.setHours(23, 59, 59, 999); // End of the day
        return o;
    }

    static dayAdd(date: Date, days: number = 1): Date {
        let o = new Date(date);
        o.setDate(o.getDate() + days);
        return o;
    }

    static monthAdd(date: Date, months: number = 1): Date {
        let o = new Date(date);
        o.setMonth(o.getMonth() + months);
        return o;
    }

    static equals(date1: Date, date2: Date): boolean {
        return !(date1 < date2) && !(date2 < date1);
    }

    /// it convert date into locale string
    static toString(
        from: Date,
        options: Intl.DateTimeFormatOptions = defaultDateTimeFormatOptions,
        locale?: string,
    ): string {
        locale = locale ?? lang?.get?.();

        /// only regards the available parameters
        options = ObjectHelper.Pick(
            options,
            "calendar",
            "dateStyle",
            "day",
            "dayPeriod",
            "era",
            "formatMatcher",
            "fractionalSecondDigits",
            "hour12",
            "hour",
            "hourCycle",
            "localeMatcher",
            "minute",
            "month",
            "numberingSystem",
            "second",
            "timeStyle",
            "timeZone",
            "timeZoneName",
            "weekday",
            "year",
        );

        const formatter = new Intl.DateTimeFormat(
            locale,
            ObjectHelper.isEmpty(options) ? defaultDateTimeFormatOptions : options,
        );
        return formatter.format(from);
    }

    /// todo modify
    static format(dateTime: Date | null, format: string): string | null {
        if (dateTime == null || DateHelper.isInvalid(dateTime)) return null;

        let _formats: string[] = [
            "dddd",
            "ddd",
            "DD",
            "D",
            "hh",
            "h",
            "HH",
            "H",
            "mm",
            "m",
            "MMMM",
            "MMM",
            "MM",
            "M",
            "ss",
            "s",
            "A",
            "a",
            "YYYY",
            "YY",
            "ZZ",
            "Z",
        ];
        let _timeNames: string[] = ["am", "pm", "AM", "PM"];

        let regex: RegExp = Array2RegExp(_formats);

        let formats: string[] = format.match(regex) || [];
        let spaces: string[] = format.split(regex);

        let year: number = dateTime.getFullYear();
        let month: number = dateTime.getMonth();
        let day: number = dateTime.getDay();
        let date: number = dateTime.getDate();
        let hour: number = dateTime.getHours();
        let minute: number = dateTime.getMinutes();
        let second: number = dateTime.getSeconds();
        let offset: number = dateTime.getTimezoneOffset() + 30;

        let dateStr: string = "";
        for (let i: number = 0; i < spaces.length; i++) {
            switch (formats[i - 1]) {
                case "dddd":
                    /// weekday long
                    dateStr += dateTime.toLocaleDateString("en-gb", { weekday: "long" });
                    break;
                case "ddd":
                    /// weekday short
                    dateStr += dateTime.toLocaleDateString("en-gb", { weekday: "short" });
                    break;
                case "DD":
                    dateStr += StringHelper.padLeft(date.toString(), 2, "0");
                    break;
                case "D":
                    dateStr += date.toString();
                    break;
                case "hh":
                    dateStr += StringHelper.padLeft((hour % 12 || 12).toString(), 2, "0");
                    break;
                case "h":
                    dateStr += (hour % 12 || 12).toString();
                    break;
                case "HH":
                    dateStr += StringHelper.padLeft(hour.toString(), 2, "0");
                    break;
                case "H":
                    dateStr += hour.toString();
                    break;
                case "mm":
                    dateStr += StringHelper.padLeft(minute.toString(), 2, "0");
                    break;
                case "m":
                    dateStr += minute.toString();
                    break;
                case "MMMM":
                    dateStr += dateTime.toLocaleDateString("en-gb", { month: "long" });
                    break;
                case "MMM":
                    dateStr += dateTime.toLocaleDateString("en-gb", { month: "short" });
                    break;
                case "MM":
                    dateStr += StringHelper.padLeft((month + 1).toString(), 2, "0");
                    break;
                case "M":
                    dateStr += (month + 1).toString();
                    break;
                case "ss":
                    dateStr += StringHelper.padLeft(second.toString(), 2, "0");
                    break;
                case "s":
                    dateStr += second.toString();
                    break;
                case "A":
                    dateStr += hour < 12 ? _timeNames[2] : _timeNames[3];
                    break;
                case "a":
                    dateStr += hour < 12 ? _timeNames[0] : _timeNames[1];
                    break;
                case "YYYY":
                    dateStr += year.toString();
                    break;
                case "YY":
                    dateStr += year.toString().slice(2);
                    break;
                case "ZZ":
                    dateStr +=
                        (offset > 0 ? "-" : "+") +
                        StringHelper.padLeft(
                            (
                                Math.floor(Math.abs(offset) / 60) * 100 +
                                (Math.abs(offset) % 60)
                            ).toString(),
                            4,
                            "0",
                        );
                    break;
                case "Z":
                    dateStr += `${offset > 0 ? "-" : "+"}${Math.abs(
                        offset / 60,
                    ).toString()}`;
                    break;
            }

            dateStr += spaces[i];
        }

        return dateStr;
    }
}

const defaultDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    dateStyle: "short",
    timeStyle: "short",
};

/**
 * Convert string array to RegExp
 * @param array
 */
function Array2RegExp(...array: string[][]): RegExp {
    let strs: string[] = [];
    strs = strs.concat(...array);
    let regex: string = "";
    for (let str of strs) {
        regex += `${str}|`;
    }
    regex = regex.replace(/\|$/, "");
    return new RegExp(regex, "g");
}
